/* eslint-disable no-console */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { setActiveJob } from '@app/actions/job';
import {
  PUT_JOB_ENDPOINT, ADD_SELF_QUEUE_JOB, GET_QUEUE_LIST, GET_QUEUE_STATS,
} from '@app/constants/endpoints';
import { putData, postData, fetchData } from '@app/hooks/react-query-helpers';
import { CaseTypeMetadataEnum } from '@app/types/create-case';
import { StateType } from '@app/types/reducer-state';

import {
  QueueRow,
  UseQueueNavigationProps,
  UseQueueMutationProps,
  UseQueueListQueryProps,
  UseQueueStatsQueryProps,
  UseQueueSelfAssignProps,
} from './types';

// =============================================
// Common Utilities
// =============================================

/**
 * Builds query parameters for API requests
 * @param caseTypeIds - Array of case type IDs
 * @param additionalParams - Additional query parameters
 * @returns URLSearchParams object with all parameters
 */
const buildQueryParams = (caseTypeIds: CaseTypeMetadataEnum[], additionalParams: Record<string, string> = {}) => {
  const queryParams = new URLSearchParams();

  caseTypeIds.forEach((caseTypeId, index) => {
    queryParams.append(`caseTypeIds[${index}]`, caseTypeId);
  });

  Object.entries(additionalParams).forEach(([key, value]) => {
    queryParams.append(key, value);
  });

  return queryParams;
};

/**
 * Logs errors with context
 * @param context - Context of the error
 * @param error - Error object
 */
const logError = (context: string, error: any) => {
  console.error(`Failed to ${context}:`, error);
};

// =============================================
// Query Configuration
// =============================================

/**
 * Common query configuration for all queue-related queries
 */
const COMMON_QUERY_CONFIG = {
  refetchOnWindowFocus: false,
  staleTime: 30 * 1000,
  refetchInterval: 60 * 1000,
  refetchIntervalInBackground: false,
  refetchOnMount: true,
};

// =============================================
// Query Invalidation
// =============================================

/**
 * Hook for invalidating queue-related queries
 */
export const useQueueQueryInvalidation = () => {
  const queryClient = useQueryClient();

  const invalidateQueueQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['queueList'] });
    queryClient.invalidateQueries({ queryKey: ['queueStats'] });
  };

  return { invalidateQueueQueries };
};

// =============================================
// Navigation Hooks
// =============================================

/**
 * Hook for handling queue navigation
 */
export const useQueueNavigation = ({ setIsServiceQueueOpen }: UseQueueNavigationProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const activeCase = useSelector((state: StateType) => state.jobs.activeJob);

  const navigateToChat = (caseId: string) => {
    if (caseId) {
      // Only set active job if it's different from current
      if (!activeCase?._id || activeCase._id !== caseId) {
        dispatch(setActiveJob(caseId));
      }
      navigate(`/chats/${caseId}`);
      setIsServiceQueueOpen(false);
    }
  };

  return { navigateToChat };
};

// =============================================
// Mutation Hooks
// =============================================

/**
 * Hook for handling queue case assignment
 */
export const useQueueMutation = ({ onSuccess, onError }: UseQueueMutationProps = {}) => {
  const { invalidateQueueQueries } = useQueueQueryInvalidation();

  const assignMutation = useMutation({
    mutationKey: ['queueList', 'queueStats'],
    mutationFn: async ({ caseId, body }: { caseId: string; body: any }) => putData(
      PUT_JOB_ENDPOINT(caseId),
      body,
    ),
    onSuccess: () => {
      invalidateQueueQueries();
      onSuccess?.();
    },
    onError: (error) => {
      logError('assign queue case - api call', error);
      onError?.(error);
    },
  });

  return assignMutation;
};

/**
 * Hook for handling queue assignment with navigation
 */
export const useQueueAssign = (props: UseQueueNavigationProps & UseQueueMutationProps) => {
  const { navigateToChat } = useQueueNavigation(props);
  const assignMutation = useQueueMutation(props);

  const assignUser = async (queueCase: QueueRow, newOperatorId: string) => {
    if (!newOperatorId || newOperatorId === 'unassigned') {
      return;
    }

    const {
      waitTime,
      ...rest
    } = queueCase;
    const body = {
      ...rest,
      reference: queueCase.referenceId,
      operatorId: newOperatorId,
    };

    try {
      await assignMutation.mutateAsync({
        caseId: queueCase._id,
        body,
      });
    } catch (error) {
      logError('assign case', error);
    }
  };

  return { assignUser, navigateToChat };
};

/**
 * Hook for handling self-assignment of queue cases
 */
export const useQueueSelfAssign = ({
  setIsServiceQueueOpen,
  caseTypeIds,
  ...props
}: UseQueueSelfAssignProps) => {
  const { navigateToChat } = useQueueNavigation({ setIsServiceQueueOpen });
  const { invalidateQueueQueries } = useQueueQueryInvalidation();

  const selfAssignMutation = useMutation({
    mutationKey: ['selfCaseAssign'],
    mutationFn: () => postData(ADD_SELF_QUEUE_JOB, { caseTypeIds }),
    onSuccess: (data) => {
      const caseId = data?.id;
      if (caseId) {
        navigateToChat(caseId);
      }
      invalidateQueueQueries();
      props.onSuccess?.(data);
    },
    onError: (error) => {
      logError('assign queue case to self - api call', error);
      props.onError?.(error);
    },
  });

  const handleSelfAssign = async () => {
    try {
      await selfAssignMutation.mutateAsync();
    } catch (error) {
      logError('self assign', error);
    }
  };

  return { handleSelfAssign };
};

// =============================================
// Query Hooks
// =============================================

/**
 * Hook for fetching queue list
 */
export const useQueueListQuery = ({
  caseTypeIds,
  limit,
  enabled,
  setHasMore,
}: UseQueueListQueryProps) => {
  const queryParams = buildQueryParams(caseTypeIds, { limit: limit.toString() });

  return useQuery({
    queryKey: ['queueList', { caseTypeIds }],
    queryFn: () => fetchData(`${GET_QUEUE_LIST}?${queryParams.toString()}`),
    ...COMMON_QUERY_CONFIG,
    enabled,
    onSuccess: (data) => {
      setHasMore(data?.length === limit);
    },
  });
};

/**
 * Hook for fetching queue statistics
 */
export const useQueueStatsQuery = ({ caseTypeIds }: UseQueueStatsQueryProps) => {
  const queryParams = buildQueryParams(caseTypeIds);

  return useQuery({
    queryKey: ['queueStats', { caseTypeIds }],
    queryFn: () => fetchData(`${GET_QUEUE_STATS}?${queryParams.toString()}`),
    ...COMMON_QUERY_CONFIG,
  });
};
